/**
 * Cookie Consent Storage Utilities
 * --------------------------------
 * Handles reading/writing consent to localStorage.
 * Safe for production use.
 */

const CONSENT_KEY = "cookie_consent";

/**
 * Default consent state
 * Necessary cookies are always true.
 */
export const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  consentedAt: null,
};

/**
 * Check if localStorage is available
 * Prevents crashes in SSR environments.
 */
const isStorageAvailable = () => {
  try {
    if (typeof window === "undefined") return false;
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely parse JSON
 */
const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Get stored consent
 * @returns {object|null}
 */
export const getConsent = () => {
  if (!isStorageAvailable()) return null;

  const stored = window.localStorage.getItem(CONSENT_KEY);
  if (!stored) return null;

  const parsed = safeParse(stored);
  if (!parsed) return null;

  return {
    ...defaultConsent,
    ...parsed,
  };
};

/**
 * Save full consent object
 * Adds timestamp automatically
 * @param {object} consent
 */
export const setConsent = (consent) => {
  if (!isStorageAvailable()) return;

  const payload = {
    ...defaultConsent,
    ...consent,
    necessary: true, // enforce always true
    consentedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
};

/**
 * Update partial consent
 * (e.g. toggle analytics only)
 * @param {object} updates
 */
export const updateConsent = (updates) => {
  const current = getConsent() || defaultConsent;

  const updated = {
    ...current,
    ...updates,
    necessary: true,
    consentedAt: new Date().toISOString(),
  };

  setConsent(updated);
};

/**
 * Clear consent completely
 * Banner will reappear
 */
export const clearConsent = () => {
  if (!isStorageAvailable()) return;
  window.localStorage.removeItem(CONSENT_KEY);
};

/**
 * Check if user has made a choice
 * @returns {boolean}
 */
export const hasConsented = () => {
  const consent = getConsent();
  return Boolean(consent?.consentedAt);
};
